import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * TemplateListPage — THE template list (D-5). One component, two modes.
 *
 * `CustomerTemplates.tsx` (1166 lines) and `TemplatesPanel.tsx` (595 lines) were two
 * implementations of one surface, and they had drifted on every axis that matters: two fetch
 * contracts, two caches, two search field sets, two ARIA vocabularies for the same List/Cards
 * toggle, two button tierings, a per-device hidden set only one of them read, an **Approve**
 * button whose onClick opened the editor, and an **Activate** action that existed only on the
 * *customer* page. AUD-1 F-11…F-16 are all that one fact.
 *
 * The mode is NOT a different component. `TemplateListCapabilities` is derived from `mode` +
 * `useSessionStore().isAdmin()`, and every admin affordance is structurally absent for a
 * customer because the capability is `false` — not because a different tree was rendered.
 * That is the property SYNTHESIS §5 lists as healthy and that a test now pins directly.
 *
 * `CustomerTemplates` and `TemplatesPanel` survive as thin wrappers, so every route, import
 * and `data-testid` keeps working. Where the two pages used different ids for the same thing,
 * BOTH are rendered — WS-D2's layout probe and 60+ existing tests key on them.
 *
 * The cloud catalog is pixel-free (Constitution III), so the preview drawer shows the
 * template's CONFIGURATION — the views it produces, their geometry and the output settings —
 * plus the base case image when the version payload carries a hosted ref.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { ActionBar } from "../ui/ActionBar.js";
import { useServices } from "../app/context.js";
import { InfoHint, Tooltip } from "../ui/Tooltip.js";
import { RowAction } from "../ui/RowAction.js";
import { useSelectionStore, selectionFromTemplate } from "../stores/selection.js";
import { useSessionStore } from "../stores/session.js";
import {
  hydrateBakedAssets, ioHydrateDeps, bakedPreviewUrl, refsForView,
  type BakedAssetRefs, type BakedAssetRefsMap,
} from "./bakedSync.js";
import { announceBakeAbsence, bakeAbsenceMessage, type BakeAbsence } from "./bakeDiagnostics.js";
import { forceSyncTemplate, savedViews } from "./forceSync.js";
import { ForceSyncPicker } from "./ForceSyncPicker.js";
import { devicePresence, type DevicePresence } from "./devicePresence.js";
import { hasMockupStored } from "../stores/mockupStore.js";
import { useHydrationStore } from "../stores/hydrationStore.js";
import {
  listSyncedTemplateIds, saveSyncedTemplate, removeSyncedTemplate, saveSyncedPhoneModels,
  saveSyncedCaseStyles,
} from "./localTemplates.js";
import { readHiddenLocalIds, writeHiddenLocalIds } from "./deviceHidden.js";
import { clearHydration } from "../stores/hydrationStore.js";
import { TemplateTable, TemplateGridCards, keyedPreviewUrl, useKeyedPreviewsOnly } from "./TemplateIndex.js";
import { isMockupOnlyTemplate } from "./wireMasks.js";
import {
  brandOf, categoryLabel, headStatusOf, humanize, modelLabel, outputFormatLabel,
  statusBadgeClass, statusLabel, styleLabel, templateSubline, versionLabel,
} from "./templateLabels.js";
import { visibleRows, rowSetCounts } from "./rowSet.js";
import {
  EMPTY_SELECTION, FACET_ORDER, activeChips, activeFacetCount, applyFacets, biggestReduction,
  clearFacets, facetOptions, splitFilters, toggleFacet,
  type ActiveChip, type FacetCtx, type FacetGroup, type FacetKey, type FacetSelection,
} from "./browseFilters.js";
import { BrowseFilterBar } from "./BrowseFilterBar.js";
import "./browse.css";
import { templateErrorMessage } from "./templateErrors.js";
import { useTemplateList } from "./useTemplateList.js";
import { useTemplateFocus } from "../stores/templateFocus.js";
import { SyncFreshness } from "../sync/SyncFreshness.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { confirmAction } from "../ui/confirmAction.js";
import { BusyButton } from "../ui/BusyButton.js";
import { Icon } from "../ui/Icon.js";
import { Drawer } from "../ui/Drawer.js";
import type { ProductTemplate, PhoneModelOption, CaseStyleOption } from "@genz/platform";
import "./customer-templates.css";

export type TemplateListMode = "customer" | "admin";
type ViewMode = "list" | "card";

export interface TemplateListPageProps {
  mode: TemplateListMode;
  /** Customer page: route to the Export workspace after staging a selection. */
  onGoExport?: (tab?: "select" | "batch") => void;
  /** Admin page: open the editor / the wizard (TemplatesPanel keeps that routing). */
  onOpenEditor?: (t: ProductTemplate) => void;
  onNewTemplate?: () => void;
  /**
   * Does this list own the page title? True on a route that is nothing but the list (the
   * customer `#browse` page); false when it is embedded under a surface that already has an
   * `<h1>` (the Editor's Templates tab), where the heading renders as an `<h2>` instead.
   */
  pageTitle?: boolean;
}

/**
 * What this viewer may DO to a row, on this page.
 *
 * Every field is a decision, not a convenience: rendering an affordance is gated on the
 * capability being true, so "an admin action cannot leak into the customer surface" is a
 * structural property of one object rather than a discipline two components had to keep.
 */
export interface TemplateListCapabilities {
  /** Admin: a real approve of a Pending head version (client.approveVersion). */
  approve: boolean;
  /** Admin: Draft → Active (submitVersion + approveVersion when the policy demands it). */
  activate: boolean;
  /** Admin: open the mask editor. */
  edit: boolean;
  /** Admin: the wizard. */
  create: boolean;
  /** Admin, and only for a template this tenant owns (`origin !== "platform"`). */
  deleteEverywhere: boolean;
  removeFromDevice: boolean;   // both
  sync: boolean;               // both
  render: boolean;             // both — "Create Mock-up"
  useForBatch: boolean;        // admin (routes to #batch rather than #export)
  /** D-4 (iii): the status chips and every non-Active row. */
  seeAllStatuses: boolean;
}

export function capabilitiesFor(mode: TemplateListMode, isAdmin: boolean): TemplateListCapabilities {
  // The AUTHORING capabilities key off the mode, not the session, because `mode: "admin"` is
  // only ever reached through the Editor route, which the shell already gates — and that is
  // exactly the pre-existing behaviour (TemplatesPanel offered Edit / Delete / New template
  // to whoever got there). `mode: "customer"` makes every one of them false, which is what
  // "an admin affordance cannot leak into the customer surface" actually requires.
  const authoring = mode === "admin";
  return {
    // The LIFECYCLE capabilities key off the SESSION, because they are offered on both
    // pages: an admin browsing the customer list could already Activate a Draft there, and
    // F-12 is the observation that the admin table was the one missing it.
    approve: isAdmin,
    activate: isAdmin,
    edit: authoring,
    create: authoring,
    deleteEverywhere: authoring,
    // Both pages can take a template off THIS device — it is a local action about local
    // disk, and D-28 requires the resulting state to be visible on both lists.
    removeFromDevice: true,
    sync: true,
    render: mode === "customer",
    useForBatch: authoring,
    // D-4 (iii): an admin sees every status wherever they are; a customer sees Active only,
    // on either page, because a Draft is not something they can use — and a chip row over
    // one value is a control that does nothing. The Mock-ups page applies the SAME gate to its
    // own union (`syncedReconcile`), so the two surfaces agree about what a customer can see;
    // `rowSetCounts` makes this page log how many it gated.
    seeAllStatuses: isAdmin,
  };
}

/** Differentiated destructive-confirm copy (Task 14 / C6) — a neutral local hide vs. an
 *  account-wide, irreversible delete, so the two are never confused. */
const REMOVE_MSG = "Remove from this device only? The cloud copy is kept and other devices are unaffected.";
const DELETE_MSG = "Delete this template for the whole account? This cannot be undone.";
/** …and the super-admin-only case: a platform LIBRARY template reaches beyond one account,
 *  so "for the whole account" would understate what the button does (review-1 I-3). */
const DELETE_LIBRARY_MSG =
  "Delete this library template for every account it is shared with? This cannot be undone.";

/** View mode is persisted PER PAGE — a customer browsing as cards and an admin working in
 *  the dense table is a real preference pair, not a drift. */
const VIEW_KEY: Record<TemplateListMode, string> = {
  customer: "genz.customerTemplates.viewMode",
  admin: "genz.templates.viewMode",
};

/**
 * WS-K3 — the facet selection, persisted PER PAGE and version-prefixed.
 *
 * Not in the URL: the app is hash-routed and the hash IS the route. `v1` is in the key so a
 * later shape change cannot be read by an older parser — and `loadFacets` still validates,
 * because a version prefix is a promise and localStorage is not a place promises are kept.
 */
const FACET_KEY: Record<TemplateListMode, string> = {
  customer: "genz.browse.facets.v1.customer",
  admin: "genz.browse.facets.v1.admin",
};

/** The server half of a facet selection is re-sent at most this often (K3-6). */
const SERVER_FILTER_DEBOUNCE_MS = 250;

/** A mock-up only template has no print area to place a design in: a batch skips it. */
const batchable = (list: ProductTemplate[]) => list.filter((t) => !isMockupOnlyTemplate(t));

/**
 * Read a stored selection, defensively.
 *
 * Anything that is not "an object whose known keys hold arrays of strings" is discarded whole.
 * A corrupt or stale value must never throw on load and must never half-apply: half a filter
 * is a surface showing rows nobody asked for, with a rail that disagrees about why.
 */
function loadFacets(mode: TemplateListMode): FacetSelection {
  try {
    if (typeof localStorage === "undefined") return EMPTY_SELECTION;
    const raw = localStorage.getItem(FACET_KEY[mode]);
    if (!raw) return EMPTY_SELECTION;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return EMPTY_SELECTION;
    const out: Record<string, readonly string[]> = { ...EMPTY_SELECTION };
    for (const key of FACET_ORDER) {
      const v = (parsed as Record<string, unknown>)[key];
      if (v === undefined) continue;
      if (!Array.isArray(v) || v.some((x) => typeof x !== "string")) return EMPTY_SELECTION;
      out[key] = v as string[];
    }
    return out as FacetSelection;
  } catch {
    return EMPTY_SELECTION;
  }
}

function saveFacets(mode: TemplateListMode, sel: FacetSelection): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(FACET_KEY[mode], JSON.stringify(sel));
  } catch { /* quota / private mode — a lost preference is not worth an exception */ }
}

/** Per-device set of locally-hidden template ids (does NOT touch the cloud). The key and the
 *  reader live in `deviceHidden.ts`, because the Mock-ups page's cloud reconcile has to honour
 *  the same list and a second copy of the string is how the two would drift apart. */
function loadHidden(): Set<string> {
  return readHiddenLocalIds();
}

/**
 * WS-K3 K3-10 — the DEFAULT view, per page: **the customer lands on Blocks, the admin on List.**
 *
 * The customer surface is the one AUD-6 §9 items 1 and 6 are about — its first screen after
 * login was a table 40 % of which was off-screen at 1024. Blocks are full-width, so there is no
 * horizontal scroller to lose the Actions column behind.
 *
 * K3-6 shipped both as `list` and escalated the flip, because four assertions in two WS-B
 * suites read it (`template-list-page.test.tsx`'s F-21 view-group vocabulary, and
 * `customer-templates.test.tsx`'s three `.tpl-table__sub` cases, which only exist in the table
 * view). Fix round 1 completes it under the coordinator's O-3 ruling: all four are
 * presentational and each pins behaviour this flip replaces, so each was re-pinned rather than
 * deleted — see progress.md.
 *
 * The ADMIN default is not in question: `templates.test.tsx:278` pins it independently and
 * D-32 ("Admin sits at Editor density") is the reason. A STORED preference still wins over
 * both, on either page.
 */
const VIEW_DEFAULT: Record<TemplateListMode, ViewMode> = {
  customer: "card",
  admin: "list",
};

function loadViewMode(mode: TemplateListMode): ViewMode {
  if (typeof localStorage === "undefined") return VIEW_DEFAULT[mode];
  const stored = localStorage.getItem(VIEW_KEY[mode]);
  if (stored === "card" || stored === "list") return stored;
  return VIEW_DEFAULT[mode];
}

/** The condensed, display-ready fields for one template row — every one of them formatted by
 *  the SHARED label module, so this page, the cards, the preview drawer and the editor print
 *  identical words. The raw `phoneModelId`/`caseStyleId` fallbacks are gone: they rendered a
 *  title-cased UUID ("040d67ee 2b53 42b1 8Ec3 …") in the Model and Style columns whenever the
 *  server couldn't resolve the joined row. */
function rowMeta(t: ProductTemplate) {
  const d = t.display;
  return {
    code: t.code,
    product: categoryLabel(d?.category),
    model: modelLabel({ model: d?.model }),
    style: styleLabel(d?.style),
    views: d?.views ?? [],
    format: outputFormatLabel(d?.outputFormat),
    ver: t.currentVersion?.versionNumber,
    // The HEAD status, so the pill agrees with the chip that filtered the row here (D-4).
    status: headStatusOf(t),
  };
}

/**
 * The version number an Approve should actually send, read from a FULL template record.
 *
 * The head wins when the server emits the pair AND it is the pending one; otherwise the
 * current version, and only when IT is pending. `undefined` means "nothing here is pending"
 * — which the caller turns into a refusal rather than a guessed `1` (review-1 I-1).
 */
export function pendingVersionNumber(full: ProductTemplate): number | undefined {
  const head = full.headVersion;
  if (head?.status === "Pending" && typeof head.versionNumber === "number") return head.versionNumber;
  const current = full.currentVersion;
  if (current?.status === "Pending" && typeof current.versionNumber === "number") return current.versionNumber;
  return undefined;
}

function useTemplateSurface({ mode, onGoExport, onOpenEditor, onNewTemplate }: TemplateListPageProps): TemplateSurface {
  const { client, io, logger, engine } = useServices();
  const isAdmin = useSessionStore((s) => s.isAdmin());
  /** Delete on a platform-LIBRARY row is a super-admin affordance — see `rowActions`. */
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin());
  const caps = useMemo(() => capabilitiesFor(mode, isAdmin), [mode, isAdmin]);
  const addToBatch = useSelectionStore((s) => s.addToBatch);
  // Select the stable array reference; derive the id list locally. (Returning a
  // freshly-mapped array from the selector would re-render on every snapshot.)
  const batchSelections = useSelectionStore((s) => s.batchSelections);
  const batchIds = useMemo(() => batchSelections.map((b) => b.templateId), [batchSelections]);

  const list = useTemplateList();
  const { templates, state, fetched, error, refresh, search, replaceRow, removeRow, setError } = list;

  /** The phone-model catalog, used for the Product→Brand→Model tree AND as the pre-WS-C
   *  Brand-column fallback. Best-effort: a failure here never blocks the browse. */
  const [phoneModels, setPhoneModels] = useState<PhoneModelOption[]>([]);
  const phoneModelsById = useMemo(() => new Map(phoneModels.map((p) => [p.id, p])), [phoneModels]);
  /** The case-style catalog. Fetched for the SAME reason as the phone models (F-7 /
   *  review-2 item 1) — it names the style in the export sheet's `Case Style` column and in
   *  the SKU — and cached locally so the offline Mock-up funnel can name it too. */
  const [caseStyles, setCaseStyles] = useState<CaseStyleOption[]>([]);

  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncedIds, setSyncedIds] = useState<Set<string>>(new Set());
  /** Force-sync-all progress ({done, total}); null = idle. Drives the header button. */
  const [syncingAll, setSyncingAll] = useState<{ done: number; total: number } | null>(null);
  const [forceSyncOpen, setForceSyncOpen] = useState(false);
  /** A lifecycle action (Activate / Approve) in flight for this row. */
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  /** The template open in the detail/preview panel (null = closed). */
  const [preview, setPreview] = useState<ProductTemplate | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** A render is resolving the full template before routing to Export. */
  const [rendering, setRendering] = useState<string | null>(null);
  /** Rows checked for the bulk render action (by templateId). */
  const [checked, setChecked] = useState<Set<string>>(new Set());
  /**
   * SELECTION MODE — off until asked for (review-1 I-4).
   *
   * K3-8 shipped the per-row checkbox unconditionally. In the list view that is a whole
   * COLUMN, and it cost the admin surface **52 px** of visible table at 1280 and 1440 (241 px
   * hidden against a 189 px base; 111 px against 59 px) — permanently, whether or not the
   * operator ever selects a row, in exchange for a bulk bar whose two actions the per-row set
   * already offers. M-01 is the mandate to shrink that overlap, so the column moved the wrong
   * way on the denser of the two surfaces.
   *
   * The affordance is not hidden: a `Select` toggle sits in the toolbar beside the view
   * switch, on both surfaces and in both views (one rule, not two), so the capability is
   * discoverable and keyboard-reachable. Leaving the mode clears the selection — a tick you
   * can no longer see is not a tick (the display half of F-10, which K3-8 already pins for the
   * filter case).
   */
  const [selecting, setSelecting] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);
  const [view, setView] = useState<ViewMode>(() => loadViewMode(mode));
  const [query, setQuery] = useState("");
  /** Per-DEVICE hidden templates. D-28: these are NEVER filtered out — they render with a
   *  state, on BOTH lists. Persisted to localStorage. */
  const [hidden, setHidden] = useState<Set<string>>(loadHidden);
  /** Outcome of the last "Remove from device" — what was actually freed, or why it wasn't.
   *  A vanished row alone can't distinguish "assets deleted" from "row hidden, disk full". */
  const [localMsg, setLocalMsg] = useState<string | null>(null);

  const q = query.trim().toLowerCase();

  /**
   * THE derived row set (F-31). The table, the cards, the tree, the tree's counts, Force
   * sync, the bulk bar, the breadcrumb and the "N selected" counter all read THIS array and
   * only this array. Five consumers used to read four different ones.
   *
   * WS-K6: `visibleRows` applies the PERMISSION gate and the search, and nothing else. The
   * status question is asked once, by the Status dropdown, downstream in `applyFacets`.
   */
  const visible = useMemo(
    () => visibleRows({
      templates, query: q, hiddenIds: hidden, canSeeAllStatuses: caps.seeAllStatuses,
    }),
    [templates, q, hidden, caps.seeAllStatuses],
  );

  /**
   * WS-K3 — THE facet selection. Exactly one, here, mutated only through `toggleFacet` /
   * `clearFacets`. G-32 asserts that no second one exists anywhere in the tree: two selections
   * are two derived sets, and two derived sets disagreeing about what is on screen is AUD-1
   * F-08 in one sentence.
   */
  const [facetSelection, setFacetSelection] = useState<FacetSelection>(() => loadFacets(mode));
  useEffect(() => { saveFacets(mode, facetSelection); }, [mode, facetSelection]);

  /** Stable id so the results region can be described BY the count line (K3-9). */
  const browseCountId = useId();

  /**
   * WS-P / D-28: does THIS device hold every saved view's mock-up, per visible row?
   *
   * `hasMockupStored` is the ASYNC persisted probe, deliberately — the sync in-memory
   * `hasMockup` is a hot-cache answer that is cold after a restart and would make the badge
   * flap. It is a bare key existence check (no decrypt, no bytes), so `savedViews(t).length`
   * reads per row is cheap.
   *
   * Run ONCE per rendered page of rows, keyed by the visible ids — never per render and never
   * per cell. Inside the row render it would be N IndexedDB reads on every keystroke in the
   * search box, and no gate would ever notice.
   */
  const [bakesLocal, setBakesLocal] = useState<Map<string, boolean>>(new Map());
  const visibleKey = visible.map((t) => t.currentVersion?.versionId ?? t.templateId).join("|");
  /**
   * The SECOND key, and the one that makes the badge actionable (review-1 i-2).
   *
   * `sync()` ends with `replaceRow(full)` — same templateId, same versionId — so `visibleKey`
   * is unchanged by the very download the "Download mock-up" button just performed. Keyed on
   * it alone the probe never re-ran: the bytes landed, the render would now be correct, and
   * the row still read `Mock-up not downloaded` with a button still offering the download.
   * The hydration store bumps `version` on every settle (markHydrating/markReady/setFailure),
   * so this re-probes exactly when a hydrate has changed what is on disk.
   */
  const hydrationVersion = useHydrationStore((s) => s.version);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = new Map<string, boolean>();
      for (const t of visible) {
        const versionId = t.currentVersion?.versionId;
        if (!versionId) { next.set(t.templateId, false); continue; }
        const views = savedViews(t);
        const present = await Promise.all(views.map((v) => hasMockupStored(versionId, v)));
        next.set(t.templateId, present.every(Boolean));
      }
      if (!cancelled) setBakesLocal(next);
    })();
    return () => { cancelled = true; };
    // Keyed by the visible ids, not the array identity: `visible` is a fresh array every
    // render and would re-probe forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, hydrationVersion]);

  /**
   * WS-P: the four inputs the badge needs, gathered here so `devicePresence` can stay pure.
   * This replaces `onDevice: (t) => !hidden.has(t.templateId)` — the hide-list alone, which
   * reported a never-synced template and a bakeless one as present on this device.
   */
  const presenceOf = useCallback(
    (t: ProductTemplate): DevicePresence => devicePresence({
      hidden: hidden.has(t.templateId),
      synced: syncedIds.has(t.templateId),
      // `undefined` (a pre-WS-P cloud) must NOT become "not-published" — see devicePresence.ts.
      hasBakedAssets: t.currentVersion?.hasBakedAssets,
      bakesLocal: bakesLocal.get(t.templateId) ?? false,
    }),
    [hidden, syncedIds, bakesLocal],
  );

  /**
   * WS-K3 — the ONE extra stage on WS-B's pipeline. Never a second pipeline:
   *
   *     templates → visibleRows(…) → applyFacets(visible, facetSelection, ctx) = shown
   *
   * `shown` is what the blocks render, what the list renders, what the bulk bar selects out of,
   * what Force sync sweeps, and what the count line reports.
   */
  const facetCtx = useMemo<FacetCtx>(() => ({
    phoneModelsById,
    hiddenIds: hidden,
    syncedIds,
    bakesLocal,
    canSeeAllStatuses: caps.seeAllStatuses,
  }), [phoneModelsById, hidden, syncedIds, bakesLocal, caps.seeAllStatuses]);

  const facetGroups = useMemo(
    () => facetOptions(visible, facetSelection, facetCtx),
    [visible, facetSelection, facetCtx],
  );
  const shown = useMemo(
    () => applyFacets(visible, facetSelection, facetCtx),
    [visible, facetSelection, facetCtx],
  );
  const chips = useMemo(() => activeChips(facetSelection, facetGroups), [facetSelection, facetGroups]);
  const facetCount = activeFacetCount(facetSelection);

  const onFacetToggle = useCallback((key: FacetKey, value: string) => {
    setFacetSelection((prev) => toggleFacet(prev, key, value));
  }, []);
  const onFacetClearAll = useCallback(() => { setFacetSelection(EMPTY_SELECTION); }, []);
  const onFacetClearGroup = useCallback((key: FacetKey) => {
    setFacetSelection((prev) => clearFacets(prev, key));
  }, []);

  /**
   * The half of the selection the SERVER is sent — **empty, by construction** (review-1 B-1).
   *
   * This is the one seam between the rail and the fetch, and `splitFilters` is where the
   * decision lives: a server-narrowed load is also the row set `facetOptions` counts over, so
   * narrowing it from the rail deletes the rail's own options and shrinks the `M` in
   * "Showing N of M". `applyFacets` filters brand and product type correctly client-side, so
   * nothing is lost but a round trip that was never needed.
   *
   * The effect stays because the seam stays: when `GET /api/templates/facets` lands (WS-C),
   * counts come from the endpoint and this is the single place that starts sending a filter
   * again. Until then the key never changes and no facet toggle ever refetches — which is
   * exactly what the fetch-contract tests pin, and what AUD-6's four-fetches-per-load
   * regression would break first.
   */
  const serverFilterRef = useRef<string>(JSON.stringify(splitFilters(facetSelection).server));
  useEffect(() => {
    const { server } = splitFilters(facetSelection);
    const key = JSON.stringify(server);
    if (key === serverFilterRef.current) return;
    serverFilterRef.current = key;
    const id = setTimeout(() => { void search(server); }, SERVER_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [facetSelection, search]);

  // Say WHY a row is not on screen, rather than letting it disappear. A customer whose synced
  // Draft is gated out of the cloud browse list still has it in the Mockup Generator, whose
  // source is the LOCAL store — it is filtered, not lost, and this line says so.
  //
  // WS-K3 ADDS the rail's own reduction to this line; it does not replace a field. WS-B's rule
  // was that a hidden row still says why it is hidden, and a row the rail removed is exactly
  // such a row.
  const gatedRef = useRef("");
  useEffect(() => {
    const counts = rowSetCounts({
      templates, query: q, hiddenIds: hidden, canSeeAllStatuses: caps.seeAllStatuses,
    });
    const hiddenByFacets = counts.visible - shown.length;
    const stamp = `${counts.gatedByStatus}|${hiddenByFacets}`;
    if (stamp === gatedRef.current) return;
    gatedRef.current = stamp;
    if (counts.gatedByStatus > 0) {
      logger?.log("info", `Templates · ${counts.gatedByStatus} template(s) hidden from this list because only Active templates are shown to non-admins. The Mock-up Generator applies the same rule, so they are not there either — an admin can activate them.`);
    }
    if (hiddenByFacets > 0) {
      logger?.log("info", `Templates · ${hiddenByFacets} template(s) hidden by the filter rail (${facetCount} filter${facetCount === 1 ? "" : "s"} active) — clear a filter to bring them back.`);
    }
  }, [templates, q, hidden, caps.seeAllStatuses, logger, shown.length, facetCount]);

  // A row dropped by the per-device hide list is reported once, by name, so it is never a
  // silent disappearance. B-7 stops dropping it (D-28) and this becomes the paint log.
  const announcedHidden = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const t of templates) {
      if (!hidden.has(t.templateId) || announcedHidden.current.has(t.templateId)) continue;
      announcedHidden.current.add(t.templateId);
      logger?.log("info", `Templates · "${t.name}" is not on this device (removed locally) — the cloud copy is kept and syncing it again restores it.`);
    }
  }, [templates, hidden, logger]);

  // The phone-model catalog. A fetch failure (or an older/fake client with no
  // listPhoneModels) degrades to an empty list — the tree then falls back to each template's
  // display.model and the Brand column to an em dash — it never blocks the browse.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await client.listPhoneModels();
        if (!cancelled) setPhoneModels(p ?? []);
        // Cache the phone-model catalog locally so the Mockup Generator can resolve
        // brand/model labels for synced templates without a network call.
        // Silent-failure sweep: a swallowed catch on a shared-state WRITE (class 6). This is
        // the catalog the Mockup Generator reads offline to resolve brand/model labels, so a
        // failed write shows up much later as em-dash Brand/Model columns in the batch tree
        // with nothing connecting the two. The browse itself is unaffected, so this logs with
        // a step name rather than raising a banner over a page that is working.
        if (p?.length) {
          await saveSyncedPhoneModels(io, p).catch((e) => {
            logger?.log("warn", "Templates · the phone-model catalog could not be cached on this device — synced templates will show “—” for brand/model offline", {
              step: "saveSyncedPhoneModels", count: p.length, error: e,
            });
          });
        }
      } catch {
        if (!cancelled) setPhoneModels([]);
      }
      // The case-style catalog, on the same best-effort terms. Its own try/catch: a failure
      // here must not cost us the phone models (and vice versa) — the resolver degrades
      // per-field, so half a catalog still names half the columns.
      try {
        const c = await client.listCaseStyles();
        if (!cancelled) setCaseStyles(c ?? []);
        if (c?.length) await saveSyncedCaseStyles(io, c).catch(() => { /* best-effort */ });
      } catch {
        if (!cancelled) setCaseStyles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [client, io, logger]);

  // Reflect which templates are already synced to THIS device (persisted across sessions),
  // so the Sync button reads its real state and the mockup flow only shows what's local.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const ids = await listSyncedTemplateIds(io);
        if (!cancelled) setSyncedIds(new Set(ids));
      } catch { /* no local store → nothing synced yet */ }
    })();
    return () => { cancelled = true; };
  }, [io]);

  // Fetch the FULL template when the preview drawer opens (A3 fix). List rows are
  // `TemplateSummary`-derived and never carry `currentVersion.payload` (see
  // `wireSummaryToTemplate`), so the drawer's view images were always empty until now.
  // Keyed on `templateId` (not the `preview` object) so the merge below — which replaces
  // `preview` with the fetched `full` template at the SAME id — does not retrigger the effect
  // and cause a refetch loop. Re-opening (even the same template) always gets a fresh fetch,
  // since closing resets `preview` to null first.
  useEffect(() => {
    const id = preview?.templateId;
    if (!id) {
      setPreviewLoading(false);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    void (async () => {
      try {
        const full = await client.getTemplate(id);
        if (cancelled) return;
        setPreview((p) => (p && p.templateId === id ? full : p));
      } catch (e) {
        if (cancelled) return;
        setPreviewError(templateErrorMessage(e, "load this template's preview"));
        // The RAW message still goes to the log — the operator-facing banner is the only
        // place the transport line is unwelcome.
        logger?.log("error", "Templates · preview fetch failed", {
          error: e instanceof Error ? e.message : String(e), templateId: id,
        });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [preview?.templateId, client, logger]);

  const setViewMode = useCallback((next: ViewMode) => {
    setView(next);
    if (typeof localStorage !== "undefined") localStorage.setItem(VIEW_KEY[mode], next);
  }, [mode]);

  /** Pull this template onto the current device: fetch its FULL record (with geometry) and
   *  PERSIST it locally — that local copy is what the Mockup Generator renders from. */
  const sync = useCallback(
    async (t: ProductTemplate) => {
      setSyncingId(t.templateId);
      setError(null);
      // F-6: a fresh press answers this press. A stale "no published mock-up" line left over
      // from the previous template would read as this one's verdict.
      setLocalMsg(null);
      try {
        // The full record (with render payload) is required — persisting it locally is the
        // whole point of "sync". A failure here fails the sync (nothing half-persisted).
        const full = await client.getTemplate(t.templateId);
        await saveSyncedTemplate(io, full); // authoritative: overwrites on re-sync
        setSyncedIds((prev) => new Set(prev).add(t.templateId));
        replaceRow(full);
        setPreview((p) => (p && p.templateId === t.templateId ? full : p));
        // A sync BACK clears the per-device hide flag: the template is on this device again,
        // so continuing to mark it "Not on this device" would be a lie (D-28).
        setHidden((prev) => {
          if (!prev.has(t.templateId)) return prev;
          const next = new Set(prev);
          next.delete(t.templateId);
          writeHiddenLocalIds(next);
          return next;
        });
        // Device registry pull (old Sync-tab behavior) — best-effort, doesn't gate the sync.
        void client.syncRegistry?.().catch(() => { /* registry pull is best-effort */ });
        // Spec 019: download + save the baked layered-mockup assets locally so render/export
        // composite ALL layers (background/texture/shell) and the baked image is the preview.
        // Without this the render falls back to a classic design-only render. The record sync
        // above is NOT undone on a failure here — but the failure must be VISIBLE: this used
        // to be a fully swallowed .catch(), so a CORS/404 asset download failed silently while
        // the row flipped to "Synced ✓" (the audit's silent-partial-sync class).
        const refs = full.currentVersion?.bakedAssets as BakedAssetRefs | null | undefined;
        // WS-P / AUD-8 LB-8: the THIRD silent skip, in the same vocabulary as the other two.
        // Pressing Sync on a template whose cloud version never published a bake persisted the
        // record, flipped the row to "Synced ✓", downloaded nothing, and said nothing — so the
        // operator's evidence was a green tick on a template that will render design-only.
        // Control flow is unchanged; there is genuinely nothing to fetch.
        //
        // F-6 (audit): the announcement went to the Error log ONLY, which is not where anyone
        // who just pressed Sync is looking — on a fresh profile the click appeared to do nothing
        // at all. The same sentence now lands on screen as a non-blocking status line: the
        // record DID sync, so this is not an error, but it is not a silent success either.
        // `announceBakeAbsence` dedupes the LOG per (version, cause); the notice is not deduped,
        // because a deliberate second press is a second question and deserves the same answer.
        const absence: BakeAbsence | null = !full.currentVersion?.versionId ? "no-version"
          : !refs ? "not-published"
          : null;
        if (absence) {
          announceBakeAbsence(logger, absence, {
            templateId: full.templateId,
            versionId: full.currentVersion?.versionId,
            templateName: full.name,
          });
          setLocalMsg(bakeAbsenceMessage(absence, full.name));
        }
        if (refs && full.currentVersion?.versionId) {
          await hydrateBakedAssets(full.currentVersion.versionId, refs, ioHydrateDeps(io), { views: savedViews(full) })
            .catch((e) => {
              const msg = e instanceof Error ? e.message : String(e);
              logger?.log("error", `Sync: template record saved but the baked layer download FAILED (${msg}) — renders on this device fall back to design-only. The message above already lists the likely causes in order; only reach for the bucket CORS policy (docs/deploy-cors.md) once the device's own network path is ruled out.`);
              setError(`"${t.name}" synced, but its mockup layers could not be downloaded (${msg}). Rendering will be missing layers on this device — check this device's network, then Force sync again.`);
            });
        }
      } catch (e) {
        setError(templateErrorMessage(e, `sync "${t.name}" to this device`));
      } finally {
        setSyncingId(null);
      }
    },
    [client, io, logger, replaceRow, setError],
  );

  /**
   * What Force sync may re-pull: EVERY template already on this device that this viewer may
   * see, whatever the page's search and filters hide — the picker has its own search, and the
   * operator picks there. It never downloads a template that is not here; the row's own Sync
   * does that.
   */
  const forceSyncable = useMemo(
    () => visibleRows({ templates, query: "", hiddenIds: hidden, canSeeAllStatuses: caps.seeAllStatuses })
      .filter((t) => syncedIds.has(t.templateId) && !hidden.has(t.templateId)),
    [templates, syncedIds, hidden, caps.seeAllStatuses],
  );

  /** FORCE-sync the picked on-device templates: fresh full record + FORCED baked-asset
   *  re-download. Serial on purpose: predictable progress, no request stampede; per-template
   *  failures don't stop the sweep. */
  const syncAll = useCallback(async (templateIds: string[]) => {
    if (syncingAll) return;
    setError(null);
    const picked = new Set(templateIds);
    const rows = forceSyncable.filter((t) => picked.has(t.templateId));
    if (rows.length === 0) return;
    setSyncingAll({ done: 0, total: rows.length });
    let failed = 0;
    // The FIRST failure's cause, carried into the banner — "see the log" alone sent the
    // operator hunting through Observability for a message the banner could just say.
    let firstError: string | null = null;
    // Templates whose RECORD synced but which carry no baked mockup at all, so no amount of
    // re-downloading can make them render as a mockup here.
    const noMockup: string[] = [];
    for (const t of rows) {
      try {
        const { template: full, hasBake } = await forceSyncTemplate(io, client, t.templateId);
        replaceRow(full);
        if (!hasBake) {
          noMockup.push(t.name);
          logger?.log("warn", `Force sync all: "${t.name}" has no baked mockup assets in the cloud — its record synced, but renders on this device will be design-only until it is re-baked in the template editor.`);
        }
      } catch (e) {
        failed++;
        const msg = e instanceof Error ? e.message : String(e);
        if (!firstError) firstError = msg;
        logger?.log("error", `Force sync all: "${t.name}" failed — ${msg}. If EVERY template fails the layer download, suspect this device's network path (DNS, VPN, proxy) before the bucket CORS policy (docs/deploy-cors.md) — a CORS refusal is indistinguishable from a network failure here (neither reaches JavaScript with a status), so what separates them is reach: CORS fails every template on EVERY device, not just this one.`);
      }
      setSyncingAll((p) => (p ? { done: p.done + 1, total: p.total } : p));
    }
    setSyncingAll(null);
    const parts: string[] = [];
    if (failed > 0) {
      parts.push(
        `${failed} of ${rows.length} template${rows.length === 1 ? "" : "s"} failed (first error: ${firstError ?? "unknown"})`,
      );
    }
    if (noMockup.length > 0) {
      parts.push(
        `${noMockup.length} could not be synced as a mockup — no baked assets in the cloud: ${noMockup.join(", ")}. ` +
        "Open each in the template editor and re-bake it (Remove background (AI) / Preview mock-up), then Save",
      );
    }
    if (parts.length > 0) {
      setError(`Force sync finished — ${parts.join(". ")}. Full list in Settings → Observability → Error log.`);
    }
  }, [forceSyncable, client, io, syncingAll, logger, replaceRow, setError]);

  /**
   * Remove a template from THIS device (never touches the cloud).
   *
   * This used to ONLY add the id to the localStorage hide-list: the synced template record,
   * its baked mockups (multi-MB PER VIEW) and its local case image all stayed on the disk
   * forever — and, because the baked rows are keyed by VERSION id, dropping the record
   * without them made the leftovers unreclaimable by any later sweep. The button hid a row
   * and freed nothing, while its own copy promised a device-local removal.
   *
   * The hide-list itself STAYS: this page lists the CLOUD catalog, and the cloud copy is
   * deliberately kept, so without a local record of "not on this device" the row would lose
   * that state on the very next `listTemplates()`. Per D-28 the row is NOT hidden — it
   * renders with a "Not on this device" badge and a "Sync back" button, on BOTH lists.
   */
  const removeFromDevice = useCallback(async (t: ProductTemplate) => {
    const id = t.templateId;
    setLocalMsg(null);
    // Hold the SAME busy flag the Sync button uses. The eviction is an AES-GCM decrypt +
    // multi-MB local delete; leaving the button idle through it made "Working…" appear for a
    // sync and not for its exact inverse, and left every caller unable to await the result.
    setSyncingId(id);
    setHidden((prev) => {
      const next = new Set(prev).add(id);
      writeHiddenLocalIds(next);
      return next;
    });
    setSyncedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    try {
      // Record + every view's baked mockup + the local case image (see removeSyncedTemplate).
      if (io?.localStore) await removeSyncedTemplate(io, id, engine);
      // …and the hydration bookkeeping, which otherwise still claims this version's assets
      // are present on a device that no longer has them (and can suppress the re-download).
      const versionId = t.currentVersion?.versionId;
      if (versionId) clearHydration(versionId);
      setLocalMsg(
        `Removed “${t.name}” from this device — its synced copy, baked mock-up assets and case image are deleted here. ` +
        `The cloud copy is kept, so you can sync it back any time.`,
      );
    } catch (e) {
      // The row is hidden either way, but a failed eviction must not read as freed space.
      setLocalMsg(
        `Hid “${t.name}”, but its local files could NOT be deleted: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setSyncingId((cur) => (cur === id ? null : cur));
    }
  }, [io, engine]);

  /** Delete (archive) a template in the CLOUD — removes it for the whole account. The server
   *  enforces ownership (platform-provided templates you don't own → 403), which is why the
   *  button is ABSENT rather than failing for a library row.
   *
   *  It also EVICTS this device's synced copy. The cloud record is the only copy of shared
   *  content and the local one is a cache (`docs/release-audit-checklist.md`, the cross-device
   *  rule) — so a delete that left the cache behind kept the template alive in the Mock-up
   *  Generator, and kept its multi-MB baked mock-ups on the disk with no record left to name
   *  them by. The eviction is best-effort AFTER the cloud call: the template IS deleted for
   *  the account either way, and the Mock-ups page's reconcile heals a device that missed
   *  this — but not until someone opens it. */
  const deleteTemplate = useCallback(async (t: ProductTemplate) => {
    setDeleting(t.templateId);
    setError(null);
    try {
      await client.archiveTemplate(t.templateId);
      removeRow(t.templateId);
      if (io?.localStore) {
        try {
          await removeSyncedTemplate(io, t.templateId, engine);
          const versionId = t.currentVersion?.versionId;
          if (versionId) clearHydration(versionId);
          setSyncedIds((prev) => {
            if (!prev.has(t.templateId)) return prev;
            const next = new Set(prev);
            next.delete(t.templateId);
            return next;
          });
        } catch (e) {
          // Not an error banner: the delete SUCCEEDED. But a device that quietly kept the
          // files is exactly the state nobody can diagnose later, so it is named.
          logger?.log("warn", `Templates · deleted "${t.name}" in the cloud, but its local copy could not be removed from this device — it will be cleared the next time the Mock-ups page syncs.`, {
            templateId: t.templateId, error: e instanceof Error ? e.message : String(e),
          });
        }
      }
      logger?.log("info", `Templates · deleted "${t.name}" for the whole account.`);
    } catch (e) {
      setError(templateErrorMessage(e, `delete "${t.name}"`));
    } finally {
      setDeleting(null);
    }
  }, [client, removeRow, setError, logger, io, engine]);

  /** Make a Draft template Active (publishable). Re-submits the saved geometry — when the
   *  approval gate is OFF (default) that activates immediately; when ON it goes Pending, so
   *  we approve it too. ONE implementation, now serving both modes. */
  const activate = useCallback(
    async (t: ProductTemplate) => {
      setBusyRowId(t.templateId);
      setError(null);
      try {
        const full = await client.getTemplate(t.templateId);
        const v = full.currentVersion;
        if (!v.payload) {
          throw new Error(`"${t.name}" has no saved geometry yet — open it in the editor first.`);
        }
        const masks = v.payload.views?.[0]?.masks ?? [];
        await client.submitVersion(t.templateId, v.versionNumber, v.versionToken, masks);
        // NO silent fallback here: guessing "approvals off" on a policy-fetch failure would
        // SKIP the approve call and strand the template in Pending with no error. Let the
        // failure surface (the outer catch shows it) so the operator can just retry.
        const policy = await client.approvalPolicy();
        if (policy.approvals_enabled) {
          await client.approveVersion(t.templateId, v.versionNumber);
        }
        const refreshed = await client.getTemplate(t.templateId);
        replaceRow(refreshed);
        setPreview((p) => (p && p.templateId === t.templateId ? refreshed : p));
        logger?.log("info", `Templates · activated "${t.name}".`);
      } catch (e) {
        setError(templateErrorMessage(e, `activate "${t.name}"`));
      } finally {
        setBusyRowId(null);
      }
    },
    [client, replaceRow, setError, logger],
  );

  /** Approve a PENDING head version — a real approval, not a detour into the editor. The
   *  admin table used to render a volt button labelled "Approve" whose onClick was
   *  `() => open(t)` with `data-testid="template-edit"` (F-11).
   *
   *  The version number comes from the RECORD, never from the summary row: the deployed
   *  cloud emits neither the head pair nor `version_number`, so `wireSummaryToTemplate`
   *  gives every row `versionNumber: 1` and approving that 409s ("Version is not pending
   *  approval") for anything past v1 (review-1 I-1). `activate()` above already fetches
   *  first; a MUTATING call must not trust a display fallback. */
  const approve = useCallback(
    async (t: ProductTemplate) => {
      setBusyRowId(t.templateId);
      setError(null);
      try {
        const full = await client.getTemplate(t.templateId);
        const n = pendingVersionNumber(full);
        // No real pending version → refuse in a sentence. Sending `1` on a guess is how a
        // row that LOOKS pending (the pill falls back to the current version when the
        // server sends no head pair) turned into a 409 the operator could not act on.
        if (typeof n !== "number") {
          throw new Error(`Couldn't find a pending version to approve — open the template.`);
        }
        await client.approveVersion(t.templateId, n);
        const refreshed = await client.getTemplate(t.templateId);
        replaceRow(refreshed);
        setPreview((p) => (p && p.templateId === t.templateId ? refreshed : p));
        logger?.log("info", `Templates · approved v${n} of "${t.name}".`);
      } catch (e) {
        setError(templateErrorMessage(e, `approve "${t.name}"`));
      } finally {
        setBusyRowId(null);
      }
    },
    [client, replaceRow, setError, logger],
  );

  /**
   * Resolve a template to a batch selection (fetching the full template for real geometry),
   * and queue it. Reuses the existing selection store — no new render path.
   */
  const resolveForBatch = useCallback(async (t: ProductTemplate) => {
    const customerId = useSessionStore.getState().session?.customerId ?? "self";
    // Fetch the FULL template for its render geometry — never fall back to the payload-less
    // list row (that masked the real failure as "no saved geometry"; see specs/022). The RAW
    // failure is rethrown so the caller — which knows what the operator was trying to do —
    // can turn it into one readable sentence.
    const full: ProductTemplate = await client.getTemplate(t.templateId);
    // F-7: hand the resolver the phone-model catalog this page already loads, so the export
    // folder tree / sheet / SKU carry the OPERATOR-chosen model name rather than its UUID.
    addToBatch(selectionFromTemplate(full, customerId, { phoneModels, caseStyles }));
  }, [client, addToBatch, phoneModels, caseStyles]);

  /** Render one template → Export's Batch tab (row + preview-drawer action). */
  const render = useCallback(async (t: ProductTemplate) => {
    setRendering(t.templateId);
    setError(null);
    try {
      await resolveForBatch(t);
      onGoExport?.("batch");   // WS-B B-9: the tab that can act on the staged selection
    } catch (e) {
      setError(templateErrorMessage(e, `create a mock-up from "${t.name}"`));
    } finally {
      setRendering(null);
    }
  }, [resolveForBatch, onGoExport, setError]);

  /** Admin's "Use for batch" — same resolve, routes to #batch. */
  const selectForBatch = useCallback(async (t: ProductTemplate) => {
    setError(null);
    try {
      await resolveForBatch(t);
    } catch (e) {
      setError(templateErrorMessage(e, `load "${t.name}" to render`));
      return;
    }
    if (typeof window !== "undefined") window.location.hash = "batch";
  }, [resolveForBatch, setError]);

  const toggleChecked = useCallback((id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * WS-K3 K3-8 — Shift+Click selects the range between the last-clicked row and this one,
   * **over `shown`**, so the range is what the operator can see rather than what the array
   * happens to hold.
   *
   * The anchor is a `useRef`, not state: it must not cause a render. A row click that sets
   * state AND re-renders the whole list to record where the shift-range would start is the
   * kind of cost nobody notices until a tenant has two thousand templates.
   */
  const rangeAnchor = useRef<string | null>(null);
  const onRowCheck = useCallback((id: string, shiftKey: boolean) => {
    const anchor = rangeAnchor.current;
    rangeAnchor.current = id;
    if (!shiftKey || !anchor || anchor === id) {
      toggleChecked(id);
      return;
    }
    const ids = shownRef.current.map((t) => t.templateId);
    const from = ids.indexOf(anchor);
    const to = ids.indexOf(id);
    if (from < 0 || to < 0) { toggleChecked(id); return; }
    const [lo, hi] = from < to ? [from, to] : [to, from];
    setChecked((prev) => {
      const next = new Set(prev);
      // A range SELECTS; it never toggles each member, because "shift-click to deselect a
      // band" is not a gesture anyone expects and it makes the result depend on what was
      // already ticked.
      for (let i = lo; i <= hi; i += 1) next.add(ids[i]!);
      return next;
    });
  }, [toggleChecked]);

  /** The checked rows that are still ON SCREEN. F-10: the bulk bar selected out of the raw
   *  `templates` array, so a filter that hid a checked row left it checked and acted on it.
   *  WS-K3 narrows this to `shown` — the rail's reduction counts too. */
  const checkedVisible = useMemo(
    () => shown.filter((t) => checked.has(t.templateId)),
    [shown, checked],
  );
  const checkedCount = checkedVisible.length;

  /**
   * F-10's DISPLAY half. WS-B stopped the bulk bar ACTING on a row a filter had hidden; what
   * it did not do is UN-TICK it, so the bar's count and the screen disagreed for as long as
   * the filter was on. A tick that is no longer on screen is no longer a tick.
   *
   * Guarded on the size so it cannot loop: it only ever shrinks `checked`, and only when
   * something actually left.
   */
  const shownRef = useRef(shown);
  shownRef.current = shown;
  useEffect(() => {
    setChecked((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(shown.map((t) => t.templateId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [shown]);

  /** M8 — "Select all N shown", tri-state. Reads `shown`, like everything else. */
  const allShownChecked = shown.length > 0 && checkedCount === shown.length;
  const someShownChecked = checkedCount > 0 && !allShownChecked;
  const toggleSelectAll = useCallback(() => {
    setChecked((prev) => {
      const ids = shownRef.current.map((t) => t.templateId);
      const all = ids.length > 0 && ids.every((id) => prev.has(id));
      if (all) {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      }
      return new Set([...prev, ...ids]);
    });
  }, []);

  /** Bulk: add every checked, VISIBLE template to the batch, then route to Export's Batch. */
  const sendCheckedToBatch = useCallback(async () => {
    if (checkedVisible.length === 0) return;
    setSendingBatch(true);
    setError(null);
    try {
      for (const t of batchable(checkedVisible)) await resolveForBatch(t);
      setChecked(new Set());
      onGoExport?.("batch");   // WS-B B-9: the tab that can act on the staged selection
    } catch (e) {
      setError(templateErrorMessage(e, "create the mock-ups"));
    } finally {
      setSendingBatch(false);
    }
  }, [checkedVisible, resolveForBatch, onGoExport, setError]);

  /**
   * Bulk **Use for batch** (admin) — the same resolve the per-row action does, then `#batch`.
   * There is deliberately no bulk Approve / Activate: a bulk lifecycle transition has server
   * semantics nobody has specified (what is the result of one 409 in fifty?). Bulk Delete is
   * `deleteChecked`, asked for by the owner.
   */
  const sendCheckedToBatchTab = useCallback(async () => {
    if (checkedVisible.length === 0) return;
    setSendingBatch(true);
    setError(null);
    try {
      for (const t of batchable(checkedVisible)) await resolveForBatch(t);
      setChecked(new Set());
      if (typeof window !== "undefined") window.location.hash = "batch";
    } catch (e) {
      setError(templateErrorMessage(e, "stage the templates for a batch"));
    } finally {
      setSendingBatch(false);
    }
  }, [checkedVisible, resolveForBatch, setError]);

  /** Bulk **Sync** — the per-row `sync`, once per checked row, reusing the header's own
   *  progress state so the surface has ONE "syncing N/M" indicator rather than two. */
  const syncChecked = useCallback(async () => {
    if (syncingAll || checkedVisible.length === 0) return;
    const rows = checkedVisible;
    setSyncingAll({ done: 0, total: rows.length });
    try {
      let done = 0;
      for (const t of rows) {
        await sync(t);
        done += 1;
        setSyncingAll({ done, total: rows.length });
      }
    } finally {
      setSyncingAll(null);
    }
  }, [syncingAll, checkedVisible, sync]);

  /** Bulk **Delete** (owner, 2026-10-15: "bulk remove all old gen and templates"): the per-row
   *  delete, once per checked row this user may delete, after ONE confirmation naming the count.
   *  A row that fails keeps its error on the banner; the others are still deleted. */
  const [deletingAll, setDeletingAll] = useState<{ done: number; total: number } | null>(null);
  const deleteChecked = useCallback(async () => {
    const rows = checkedVisible.filter((t) => isSuperAdmin || !(t.origin === "platform" || t.isLibrary));
    if (deletingAll || rows.length === 0) return;
    const many = rows.length === 1 ? "this template" : `these ${rows.length} templates`;
    if (!(await confirmAction(`Delete ${many} for the whole account? This cannot be undone.`))) return;
    setDeletingAll({ done: 0, total: rows.length });
    try {
      let done = 0;
      for (const t of rows) {
        await deleteTemplate(t);
        done += 1;
        setDeletingAll({ done, total: rows.length });
      }
      setChecked(new Set());
    } finally {
      setDeletingAll(null);
    }
  }, [checkedVisible, isSuperAdmin, deletingAll, deleteTemplate]);

  /**
   * The bulk-select checkbox in the shared index's leading slot.
   *
   * WS-K3 K3-8 removes the `caps.render` gate, so the ADMIN list gets it too. The old reason
   * ("an admin's row actions are already crowded and the admin path is the per-row Use for
   * batch") is an argument against a bulk ACTION, not against selection — and the admin bulk
   * bar now offers exactly the two non-destructive operations the row set already has.
   *
   * `onClick` rather than `onChange`, because `onChange` carries no `shiftKey` and the range
   * gesture is the whole point. `stopPropagation` keeps the click off the row.
   */
  const rowCheckbox = useCallback(
    (t: ProductTemplate): ReactNode => (
      <label className="ctpl-row__check">
        <input
          type="checkbox"
          checked={checked.has(t.templateId)}
          onChange={() => { /* state is driven by onClick, which knows about Shift */ }}
          onClick={(e) => {
            e.stopPropagation();
            onRowCheck(t.templateId, e.shiftKey);
          }}
          aria-label={`Select ${t.name} for batch`}
          data-testid="customer-template-check"
        />
      </label>
    ),
    [checked, onRowCheck],
  );

  /* The authoring page ROUTES a row, the customer page PREVIEWS it — and nothing in between.
     `caps.edit && onOpenEditor` used to fall through to the drawer when the callback was
     absent, which no route ever is (WS-Z1 fix round 2, L-1); the fall-through only set state
     the admin surface no longer renders, so the branch goes with the render. */
  const openRow = useCallback((t: ProductTemplate) => {
    if (caps.edit) onOpenEditor?.(t);
    else setPreview(t);
  }, [caps.edit, onOpenEditor]);

  const focusId = useTemplateFocus((f) => f.templateId);
  // The cached paint may predate the template: give up only once the network has answered.
  useEffect(() => {
    if (!focusId) return;
    const t = templates.find((x) => x.templateId === focusId);
    if (!t && !fetched) return;
    useTemplateFocus.setState({ templateId: undefined });
    if (t) openRow(t);
    else setLocalMsg("That template is not in your list yet. Refresh in a moment.");
  }, [focusId, fetched, templates, openRow]);

  /**
   * The ONE per-row action set, gated by the capability object. An affordance a capability
   * forbids is never CONSTRUCTED, so it cannot leak into the other mode.
   *
   * Tiering per DESIGN.md's One Voice Rule (DESIGN.md:167): no `btn--primary` in any row, on
   * either page. Volt lives on the bulk bar's "Create Mock-up (N)" and on the admin header's
   * "New template".
   */
  const rowActions = useCallback(
    (t: ProductTemplate): ReactNode => {
      const offDevice = hidden.has(t.templateId);
      const synced = syncedIds.has(t.templateId);
      // WS-P: synced, the cloud AFFIRMATIVELY published a bake, this device does not have it —
      // the one state whose row used to offer only "Unsync" when what it needed was a download.
      //
      // `=== true`, not the `bake-missing` presence: that presence is also reached when the
      // cloud did not say (a pre-WS-P server, where `hasBakedAssets` is `undefined`), and
      // there a genuinely classic template with no bake at all would have lost its Unsync
      // control in exchange for a download that has nothing to fetch. The BADGE still warns on
      // the unknown case; only the ACTION requires certainty.
      const bakeMissing =
        t.currentVersion?.hasBakedAssets === true && presenceOf(t) === "bake-missing";
      const syncing = syncingId === t.templateId;
      const isRendering = rendering === t.templateId;
      const rowBusy = busyRowId === t.templateId;
      const head = headStatusOf(t);
      // A library row 403s on delete server-side — the button is ABSENT rather than failing.
      // For a SUPER ADMIN it does not: the server's rule is `can_access_tenant`
      // (`router.py:513-517` → `:171`), which `deps.py:76` passes for super_admin, and at
      // base every row offered Delete. Gating them out made the person who authors the
      // shared library the one role that could no longer retire a template from it
      // (review-1 I-3).
      const library = t.origin === "platform" || !!t.isLibrary;
      const owned = isSuperAdmin || !library;
      return (
        <>
          {caps.approve && head === "Pending" && (
            <RowAction
              label={rowBusy ? "Approving…" : "Approve"}
              hint="Approve this version — it becomes the active one"
              icon="check"
              onClick={() => void approve(t)}
              disabled={rowBusy}
              data-testid="template-approve"
            />
          )}
          {caps.activate && head === "Draft" && (
            <RowAction
              label={rowBusy ? "Activating…" : "Activate"}
              hint="Make this Draft Active (publishable)"
              icon="power"
              onClick={() => void activate(t)}
              disabled={rowBusy}
              data-testid={mode === "admin" ? "template-activate" : "customer-template-activate"}
            />
          )}
          {caps.edit && (
            <RowAction
              label="Edit masks"
              icon="editor"
              tone="quiet"
              onClick={() => onOpenEditor?.(t)}
              data-testid="template-edit"
            />
          )}
          {/* The device control.
              On the CUSTOMER list it is the full toggle it has always been — Sync ⇄ Unsync —
              because that list has no other device affordance.
              On the ADMIN list it appears ONLY to bring a removed row back ("Sync back"),
              because that list's device control is the CONFIRMED "Remove from device" below:
              a plain toggle beside it would be a second control on the same axis, differing
              only in whether it asks first. Keeping both also pushed the admin row to six
              buttons, which clipped Delete out of the scroller at 1024 — caught by WS-D2's
              layout probe, which this wave must leave at `clipped=[]`.

              WS-Z9: FOUR STATES, FOUR ICONS. The label used to read "Synced · Unsync" — a
              state and a verb in one string, because a wide text button had room for both.
              An icon button does not, and it does not need to: the STATE is now which icon
              is drawn (a download arrow, a sync loop, a minus) and the VERB is the tooltip.
              Colour is carrying none of it, which is the rule state has always owed. */}
          {caps.sync && (mode === "customer" || offDevice) && (
            <RowAction
              icon={bakeMissing ? "download" : synced && !offDevice ? "minus-circle" : "refresh"}
              label={
                syncing ? "Working…"
                  : bakeMissing ? "Download mock-up"
                  : offDevice ? "Sync back"
                  : synced ? "Unsync"
                  : "Sync"
              }
              // F-5: the four sentences this button used to carry in its `title`. The verb
              // above is the NAME; this is what it means on the row it is standing on.
              hint={
                bakeMissing ? "Download this template's mock-up to this device"
                  : offDevice ? "Sync it back to this device"
                  : synced ? "Remove from this device"
                  : "Sync to this device"
              }
              onClick={() =>
                // WS-P: a synced row whose MOCK-UP never arrived must be able to fetch it. The
                // button read "Synced · Unsync" — the only offer on the one row that needed a
                // download, so the operator's sole route to the missing bake was to unsync and
                // sync again. `sync` re-fetches the record and awaits the hydrate; the assets
                // are absent locally, so no "already local" fast path swallows it.
                bakeMissing ? void sync(t)
                  : synced && !offDevice ? void removeFromDevice(t)
                  : void sync(t)}
              disabled={syncing}
              data-testid={mode === "admin" ? "template-sync" : "customer-template-sync"}
            />
          )}
          {/* `.btn--success` retired (F-81). "In batch" is an already-done state, not a loud
              invitation, and the icon carries it — colour alone never did (AUD-7 §6.9). A volt
              fill here would have put one on every row.
              WS-Z9: `BusyButton` rather than `RowAction`, because this is the one row action
              with a double-press guard; it is given the same three attributes by hand and the
              busy state is the spinner alone (the icon plus a spinner does not fit 30×30).
              F-4: the done state is `check-circle`, not `check`. A Pending template that is
              already in a batch renders Approve and this button IN THE SAME CELL, and they
              were the same glyph — two identical marks a row apart meaning two different
              things. The ring is the difference, as it is for `minus-circle` against `minus`. */}
          {caps.useForBatch && !isMockupOnlyTemplate(t) && (
            <Tooltip text={batchIds.includes(t.templateId) ? "In batch" : "Use for batch"}>
              <BusyButton
                className="btn btn--sm btn--icon"
                aria-label={batchIds.includes(t.templateId) ? "In batch" : "Use for batch"}
                busyLabel={<span className="btn-spinner" aria-hidden="true" />}
                onAction={() => selectForBatch(t)}
                data-testid="template-use-for-batch"
              >
                <Icon name={batchIds.includes(t.templateId) ? "check-circle" : "plus"} size={16} />
              </BusyButton>
            </Tooltip>
          )}
          {caps.render && !isMockupOnlyTemplate(t) && (
            <RowAction
              label={isRendering ? "Creating…" : "Create Mock-up"}
              hint="Create a mock-up in Export"
              icon="mockups"
              onClick={() => void render(t)}
              disabled={isRendering}
              data-testid="customer-template-render"
            />
          )}
          {/* The admin list's explicit, CONFIRMED "Remove from device" — differentiated from
              Delete-everywhere by its own copy (Task 14 / C6). The customer page reaches the
              same action through the Sync toggle, which already reads as its inverse. */}
          {caps.removeFromDevice && caps.edit && !offDevice && (
            <ConfirmPopover
              message={REMOVE_MSG}
              confirmLabel="Remove from device"
              onConfirm={() => void removeFromDevice(t)}
            >
              {(toggle) => (
                <RowAction
                  label="Remove"
                  // F-5: Remove and Delete are ADJACENT in this one cell. As words their own
                  // copy told them apart; as icons the tooltip has to, so it carries the
                  // sentence each of them used to hold in its `title`.
                  hint="Remove from this device only (stays in the cloud)"
                  icon="minus-circle"
                  tone="quiet"
                  onClick={toggle}
                  data-testid="template-remove-local"
                />
              )}
            </ConfirmPopover>
          )}
          {caps.deleteEverywhere && (
            owned ? (
              <ConfirmPopover
                message={library ? DELETE_LIBRARY_MSG : DELETE_MSG}
                confirmLabel="Delete everywhere"
                danger
                onConfirm={() => void deleteTemplate(t)}
              >
                {(toggle) => (
                  <RowAction
                    label={deleting === t.templateId ? "Deleting…" : "Delete"}
                    hint="Delete from the cloud (whole account)."
                    icon="trash"
                    tone="danger"
                    onClick={toggle}
                    disabled={deleting === t.templateId}
                    data-testid="template-delete"
                  />
                )}
              </ConfirmPopover>
            ) : (
              /* Not an action — a NOTE in the action cell, saying why the row has no Delete.
                 It keeps its own testid and its explanation, and it stops being the widest
                 thing in the column: the padlock is the same 16px glyph as every icon beside
                 it, and `Tooltip` gives it the sentence on hover.

                 F-6: and on FOCUS, because `tabIndex={0}` puts it in the tab order. It is the
                 only thing on the row that says why the row has no Delete, and with nothing
                 focusable to open the tooltip a keyboard user had no route to it — the same
                 defect as a `title` on a `disabled` button, which Tab skips. It stays a note
                 and does not become a button: a button that does nothing when pressed is
                 worse than no button. */
              <Tooltip text="This template comes from the shared platform library — only its owner can delete it.">
                <span
                  className="faint ctpl-row__note"
                  data-testid="template-delete-blocked"
                  role="img"
                  aria-label="Library — only its owner can delete it"
                  tabIndex={0}
                >
                  <Icon name="lock" size={16} />
                </span>
              </Tooltip>
            )
          )}
        </>
      );
    },
    [
      caps, isSuperAdmin, hidden, syncedIds, syncingId, rendering, busyRowId, deleting, batchIds, mode,
      approve, activate, onOpenEditor, sync, removeFromDevice, selectForBatch, render, deleteTemplate,
      // WS-P: the device button's label depends on the presence probe, so the row must
      // re-render when it settles — otherwise "Download mock-up" never appears.
      presenceOf,
    ],
  );

  /**
   * The row sub-line.
   *
   * The VERSION prefix is admin-only, and deliberately: the authoring list's whole job is
   * which version you are about to edit, while the customer list leads with the template code
   * — the thing a customer quotes in an order. Both are pinned by their own tests
   * (`templates.test.tsx` wants `Version 3 · IP15-CLR · …`, `customer-templates.test.tsx`
   * wants `^MY-CODE · `), and they are two correct answers rather than a drift.
   *
   * D-28's "Not on this device" state lands in B-7 with the rest of the gated action set.
   */
  const rowSubline = useCallback((t: ProductTemplate): ReactNode => {
    const facts = templateSubline(t);
    return caps.edit ? `${versionLabel(t.currentVersion?.versionNumber)} · ${facts}` : facts;
  }, [caps.edit]);

  /** The results, rendered through the SHARED index. Called ONCE, with `shown` — the rail's
   *  output and the only derived row set on this surface (F-31). */
  const renderRows = useCallback(
    (rows: ProductTemplate[]) => {
      if (rows.length === 0) {
        /*
         * WS-K3 K3-11 — the FILTER dead end, and the one sentence in this wave that turns a
         * dead end into a next step.
         *
         * "No templates match these filters" alone is the product saying "nothing here". The
         * second line NAMES the group doing the most hiding and says what removing it would
         * show — a number that is already computed (`applyFacets` with one group cleared), so
         * the sentence costs nothing. It is OMITTED when exactly one group is active, because
         * "clearing your only filter would show everything" is not information.
         *
         * review-1 I-5 — and it must be TRUE on the path that actually reaches it. The rail
         * alone cannot reach zero (M7's disabled-zero rule guarantees it), so this state is
         * reached from UPSTREAM: the search box or a status chip. On the search path the old
         * copy — "Clear it to see every template again" beside a button that deliberately
         * leaves the search alone — promised something the button does not deliver, and the
         * comparison could read "would show 0" because `visible` is already empty.
         *
         * So: the sentence names every narrowing thing that is on, the comparison is dropped
         * when it would offer zero, the title counts the filters it has, and when a search is
         * also active there is a second button that clears THAT.
         *
         * WS-K6 finished the job. "Clear it to see every template again" was still false on the
         * path this branch is reached from without a search — the status CHIP was a second
         * narrowing state `Clear all filters` could not touch, so the button left rows hidden
         * and the promise unkept. One status control makes the promise true, and a true promise
         * can afford to say WHICH groups are keeping it: "Status is narrowing this" is the half
         * that turns "nothing here" into "here is the control to go and undo".
         *
         * This branch is FIRST: a search and a filter can both be active, and the filter is the
         * one the operator just touched.
         */
        if (facetCount > 0) {
          const best = biggestReduction(visible, facetSelection, facetCtx);
          const searching = q !== "";
          const one = facetCount === 1;
          const filterWord = one ? "filter" : "filters";
          const title = searching
            ? `No templates match this search and ${one ? "filter" : "these filters"}`
            : `No templates match ${one ? "this filter" : "these filters"}`;
          /* The groups doing the narrowing, named. Read off `facetGroups` rather than off the
             selection alone, for `activeChips`' reason: a stale stored selection for a group
             this viewer does not have (a customer's `status`) filters nothing, so naming it
             would send the operator looking for a control that is not on screen. */
          const narrowing = facetGroups
            .filter((g: FacetGroup) => facetSelection[g.key].length > 0)
            .map((g: FacetGroup) => g.legend);
          const namedGroups = narrowing.length > 1
            ? `${narrowing.slice(0, -1).join(", ")} and ${narrowing[narrowing.length - 1]}`
            : narrowing[0] ?? "";
          const clearPromise = `Clear ${one ? "it" : "them"} to see every template again.`;
          const hint = [
            // The comparison, only while it still offers something. `biggestReduction` is
            // computed over `visible`, so with a search on top it can honestly be 0 — and
            // "Clearing Brand would show 0" is a dead end wearing a next step's clothes.
            best && best.wouldShow > 0
              ? `${facetCount} filters are active. Clearing ${best.legend} would show ${best.wouldShow}.`
              : searching ? ""
                : namedGroups
                  ? `${namedGroups} ${narrowing.length > 1 ? "are" : "is"} narrowing this. ${clearPromise}`
                  : clearPromise,
            // The half the old copy left out, and the reason it was false.
            searching
              ? `The search for “${query.trim()}” is narrowing this too, so clearing the ${filterWord} alone will not bring rows back — clear the search as well.`
              : "",
          ].filter(Boolean).join(" ");
          return (
            <EmptyState
              icon="search"
              title={title}
              hint={hint}
              testId="templates-empty-filters"
              action={
                <>
                  <button
                    type="button"
                    className="btn btn--ghost empty-state__action"
                    onClick={onFacetClearAll}
                    data-testid="templates-empty-filters-clear"
                  >
                    Clear all filters
                  </button>
                  {searching && (
                    <button
                      type="button"
                      className="btn btn--ghost empty-state__action"
                      onClick={() => setQuery("")}
                      data-testid="templates-empty-filters-clear-search"
                    >
                      Clear search
                    </button>
                  )}
                </>
              }
            />
          );
        }
        if (q !== "" && templates.length > 0) {
          return (
            <EmptyState
              icon="sparkle"
              title={<>No templates match “{query.trim()}”</>}
              hint="Try a different name, or clear the search."
              testId="templates-empty-search"
              action={
                <button type="button" className="btn btn--ghost empty-state__action" onClick={() => setQuery("")}>
                  Clear search
                </button>
              }
            />
          );
        }
        if (mode === "customer") {
          /* K3-11: the copy, not the testid. `customer-templates.test.tsx:497` asserts this
             element for an EMPTY CATALOG (`listTemplates → []`), where "No templates match
             those filters" named a cause that did not exist — there were no filters and no
             templates. A customer cannot create one, so there is no action to offer either. */
          return (
            <EmptyState
              icon="image"
              title="No templates available yet"
              hint="Templates appear here once your team publishes them."
              testId="customer-templates-empty"
            />
          );
        }
        return (
          <EmptyState
            icon="sparkle"
            title={templates.length > 0 ? "No templates on this device" : "No templates yet"}
            hint="Generate your first template — reference photos in, a mock-up-ready template out."
            testId="templates-empty"
            action={caps.create ? (
              /* The SAME one commit as the action bar's, drawn where an empty catalog puts it.
                 It carries the same testid: the two never render together, and a gate that
                 opens the wizard by that name must find it on an empty catalog too. */
              <button
                type="button"
                className="btn btn--primary empty-state__action"
                onClick={onNewTemplate}
                data-testid="templates-generate"
              >
                <Icon name="sparkle" size={16}/> New template
              </button>
            ) : undefined}
          />
        );
      }
      const isQueued = (t: ProductTemplate) => batchIds.includes(t.templateId);
      const rowProps = {
        rows,
        onOpen: openRow,
        openTitle: caps.edit ? "Open to edit / re-mask" : "Open preview",
        actions: rowActions,
        queued: isQueued,
        // D-3 (iii): Brand is its own COLUMN, resolved server-first and then through the
        // phone-model catalog this page has already loaded — never guessed.
        brand: (t: ProductTemplate) => brandOf(t, phoneModelsById),
        // D-28: a row this device cannot fully render is MARKED, never dropped. WS-P: the flag
        // became a five-state value, because "you removed it", "never synced here", "the
        // mock-up has not downloaded" and "no mock-up was ever published" need four different
        // sentences and four different offers — and used to get one silence.
        presence: presenceOf,
        // review-1 m-5: on the customer BROWSE list the row's own button already reads "Sync",
        // so a neutral `Not synced here` badge beside it landed on nearly every row and
        // diluted the two warn states this wave added. Quieted here only; the admin list has
        // no such button, so its badge is the only statement and stays visible. The DOM
        // marker is identical in both.
        quietNotSynced: mode !== "admin",
        // Both pages' testids are rendered so WS-D2's layout probe and every existing test
        // keep working, whichever page they were written against.
        listTestId: mode === "admin" ? "templates-list" : "customer-templates-list",
        itemTestId: mode === "admin" ? "template-item" : "customer-template-item",
        openTestId: mode === "admin" ? "template-open" : "customer-template-open",
        /* F-8 — the seam `design-gate.mjs`'s G-26o opens a dialog through, and the ONLY one it
           has. `openRow` routes to the Editor on the authoring page (`caps.edit`) and opens the
           preview Drawer on the customer page, so the hook is declared here, where that branch
           is decided, and is absent on `admin` — a gate that guessed which control opens
           something would drive the product instead of measuring it. */
        openGateOpen: mode === "admin" ? undefined : "customer-template-preview",
      } as const;
      if (view === "list") {
        return (
          <TemplateTable
            {...rowProps}
            leading={selecting ? rowCheckbox : undefined}
            subline={rowSubline}
            model={(t) => rowMeta(t).model}
            className={mode === "admin" ? "templates__list" : "ctpl-table"}
          />
        );
      }
      return (
        <TemplateGridCards
          {...rowProps}
          leading={selecting ? rowCheckbox : undefined}
          /* K3-5's identity line: `brand · model · product type`. The COMPONENT's default is
             `model · style` and stays that way — four suites mount it without this prop and
             pin the default string — so the browse page supplies the words through the
             injection point the file was designed around. */
          meta={(t) => `${brandOf(t, phoneModelsById)} · ${rowMeta(t).model} · ${rowMeta(t).product}`}
          versions={rowSubline}
          checked={(t) => checked.has(t.templateId)}
          className={mode === "admin" ? "" : "ctpl-cards"}
        />
      );
    },
    [
      view, mode, caps, selecting, rowCheckbox, rowActions, rowSubline, openRow, batchIds, phoneModelsById,
      hidden, q, query, templates.length, onNewTemplate,
      // K3-11's filter dead-end branch reads all four, and WS-K6's naming reads the groups.
      facetCount, facetSelection, facetCtx, facetGroups, visible, onFacetClearAll,
      // K3-8: the block's `data-checked` reads this directly, so a stale closure would leave
      // the tint and the marker one click behind the bulk bar's count.
      checked,
      // The badge reads `presence: presenceOf`. `rowActions` already depends on it, so this
      // is covered transitively today — named explicitly so the coverage survives a change to
      // `rowActions`, and so the branch stops carrying an exhaustive-deps warning.
      presenceOf,
    ],
  );

  const firstLoad = state === "loading" && templates.length === 0;

  return {
    mode, caps,
    view, setViewMode, selecting,
    toggleSelecting: () => { setSelecting((prev) => !prev); setChecked(new Set()); },
    loading: state === "loading",
    refresh, syncingAll,
    forceSyncCount: forceSyncable.length,
    openForceSync: () => setForceSyncOpen(true),
    forceSyncPicker: forceSyncOpen && (
      <ForceSyncPicker
        rows={forceSyncable.map((t) => ({
          templateId: t.templateId, name: t.name, code: t.code,
          brand: brandOf(t, phoneModelsById), model: modelLabel({ model: t.display?.model }),
        }))}
        onConfirm={(ids) => { setForceSyncOpen(false); void syncAll(ids); }}
        onClose={() => setForceSyncOpen(false)}
      />
    ),
    visibleCount: visible.length,
    searchPlaceholder: "Search templates, brands, models…",
    query, setQuery,
    facetGroups, facetSelection, onFacetToggle, onFacetClearGroup, onFacetClearAll,
    error, localMsg, firstLoad,
    browseCountId, shown, loadedCount: templates.length,
    allShownChecked, someShownChecked, toggleSelectAll, chips,
    renderRows,
    checkedCount,
    clearChecked: () => setChecked(new Set()),
    syncChecked, sendCheckedToBatch, sendCheckedToBatchTab, sendingBatch,
    deleteChecked, deletingAll,
    onNewTemplate,
    preview: preview && (
      <TemplatePreview
        template={preview}
        synced={syncedIds.has(preview.templateId)}
        busy={syncingId === preview.templateId}
        rendering={rendering === preview.templateId}
        loading={previewLoading}
        fetchError={previewError}
        canRender={caps.render && !isMockupOnlyTemplate(preview)}
        onRender={() => void render(preview)}
        onSync={() => void sync(preview)}
        onUnsync={() => void removeFromDevice(preview)}
        onClose={() => setPreview(null)}
      />
    ),
  };
}

/**
 * The state ONE engine hands to TWO surfaces.
 *
 * Rule B: `#browse` and Editor ▸ Templates are two pages with two jobs — pick-and-use versus
 * authoring — and sharing a LAYOUT was what made them one component with `mode ===` written
 * through it. What they legitimately share is the derived row set and the operations over it,
 * which is this object; what they do not share is a single line of composition.
 */
interface TemplateSurface {
  mode: TemplateListMode;
  caps: TemplateListCapabilities;
  view: ViewMode;
  setViewMode: (next: ViewMode) => void;
  selecting: boolean;
  toggleSelecting: () => void;
  loading: boolean;
  refresh: () => Promise<void>;
  syncingAll: { done: number; total: number } | null;
  /** Every on-device template this viewer may see — what Force sync may re-pull. */
  forceSyncCount: number;
  openForceSync: () => void;
  forceSyncPicker: ReactNode;
  visibleCount: number;
  searchPlaceholder: string;
  query: string;
  setQuery: (next: string) => void;
  facetGroups: FacetGroup[];
  facetSelection: FacetSelection;
  onFacetToggle: (key: FacetKey, value: string) => void;
  onFacetClearGroup: (key: FacetKey) => void;
  onFacetClearAll: () => void;
  error: string | null;
  localMsg: string | null;
  firstLoad: boolean;
  browseCountId: string;
  shown: ProductTemplate[];
  loadedCount: number;
  allShownChecked: boolean;
  someShownChecked: boolean;
  toggleSelectAll: () => void;
  chips: ActiveChip[];
  renderRows: (rows: ProductTemplate[]) => ReactNode;
  checkedCount: number;
  clearChecked: () => void;
  syncChecked: () => Promise<void>;
  sendCheckedToBatch: () => Promise<void>;
  sendCheckedToBatchTab: () => Promise<void>;
  sendingBatch: boolean;
  deleteChecked: () => Promise<void>;
  deletingAll: { done: number; total: number } | null;
  onNewTemplate?: () => void;
  preview: ReactNode;
}

/* ══════════════════════ the pieces BOTH surfaces are built from ══════════════════════
 *
 * Shared because they are the same OBJECT on both pages, not because two pages happened to
 * be one component: the results region is the row set with a count line over it, and a page
 * that drew its own would be a second answer to "what is on screen".
 */

/** The view switch + Select toggle + Refresh — the three controls that change how the rows
 *  are PRESENTED rather than what is done to them. Both pages carry all three; the action
 *  bar at the foot of the page carries everything that commits. */
function SurfaceViewControls({ s }: { s: TemplateSurface }) {
  const admin = s.mode === "admin";
  return (
    <>
      {/* F-21: ONE ARIA vocabulary. The customer page used role="tablist"/"tab"/
          aria-selected, which is wrong — there is no tabpanel, no aria-controls and no
          roving tabindex, and the content is the same rows either way. The admin page's
          role="group" + aria-pressed wins. */}
      <div className="seg ctpl-seg" role="group" aria-label="View mode" data-testid={admin ? "templates-view-toggle" : "customer-templates-view-toggle"}>
        <button
          type="button"
          className={`seg__btn${s.view === "list" ? " seg__btn--on" : ""}`}
          aria-pressed={s.view === "list"}
          onClick={() => s.setViewMode("list")}
          data-testid={admin ? "templates-view-list" : "customer-templates-view-list"}
        >
          <span className="seg__glyph" aria-hidden="true"><Icon name="list" size={16}/></span> List
        </button>
        <button
          type="button"
          className={`seg__btn${s.view === "card" ? " seg__btn--on" : ""}`}
          aria-pressed={s.view === "card"}
          onClick={() => s.setViewMode("card")}
          data-testid={admin ? "templates-view-card" : "customer-templates-view-card"}
        >
          <span className="seg__glyph" aria-hidden="true"><Icon name="grid" size={16}/></span> Blocks
        </button>
      </div>
      {/* review-1 I-4 — the Select column is a MODE, not furniture. */}
      <button
        type="button"
        className="btn btn--sm btn--ghost browse-selectmode"
        aria-pressed={s.selecting}
        onClick={s.toggleSelecting}
        data-testid="browse-select-mode"
        title={s.selecting ? "Hide the row checkboxes" : "Show a checkbox on every row, for bulk actions"}
      >
        <Icon name="check" size={16}/> {s.selecting ? "Done" : "Select"}
      </button>
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        onClick={() => void s.refresh()}
        disabled={s.loading}
        data-testid={admin ? "templates-refresh" : "customer-templates-refresh"}
        aria-label="Refresh template list"
        title="Re-pull the latest templates from the cloud"
      >
        <Icon name="refresh" size={16}/> Refresh
      </button>
    </>
  );
}

/**
 * Force sync — the manual escape hatch when cross-device sync looks stale. It opens the
 * picker of every template on this device; only the ticked ones are re-downloaded.
 *
 * Z1 rule 7 moved it OUT of the header toolbar and into the action bar: it is a commit over
 * rows on screen, which is what the bar at the foot of the page is for.
 */
function ForceSyncButton({ s }: { s: TemplateSurface }) {
  return (
    <button
      type="button"
      className="btn btn--sm btn--ghost"
      onClick={s.openForceSync}
      disabled={!!s.syncingAll || s.forceSyncCount === 0}
      data-testid="ctpl-sync-all"
      title="Pick which templates on this device to re-download with their mock-ups. Only templates already on this device are synced."
    >
      <Icon name="download" size={16} />{" "}
      {s.syncingAll ? `Syncing ${s.syncingAll.done}/${s.syncingAll.total}…` : "Force sync"}
    </button>
  );
}

/** The selection cluster inside the action bar: the announced count, Clear, and the bulk
 *  operations that are a SUBSET of the per-row set (no bulk approve / activate — see the note
 *  on `rowActions`). Rendered only while something is checked, so `N selected`
 *  is an announcement and not a permanent "0 selected". */
function SelectionCluster({ s }: { s: TemplateSurface }) {
  if (s.checkedCount === 0) return null;
  return (
    <div className="ctpl-bulkbar" role="status" aria-live="polite" data-testid="customer-templates-bulkbar">
      <span className="ctpl-bulkbar__count">{s.checkedCount} selected</span>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={s.clearChecked}
        data-testid="customer-templates-clear-selection"
      >
        Clear
      </button>
      <button
        type="button"
        className="btn btn--sm"
        onClick={() => void s.syncChecked()}
        disabled={!!s.syncingAll}
        data-testid="browse-bulk-sync"
      >
        <Icon name="download" size={16} />{" "}
        {s.syncingAll ? `Syncing ${s.syncingAll.done}/${s.syncingAll.total}…` : `Sync (${s.checkedCount})`}
      </button>
      {s.caps.useForBatch && (
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => void s.sendCheckedToBatchTab()}
          disabled={s.sendingBatch}
          data-testid="browse-bulk-batch"
        >
          {s.sendingBatch ? "Staging…" : `Use for batch (${s.checkedCount})`}
        </button>
      )}
      {s.caps.deleteEverywhere && (
        <button
          type="button"
          className="btn btn--sm btn--danger"
          onClick={() => void s.deleteChecked()}
          disabled={!!s.deletingAll}
          title="Delete the selected templates from the cloud (whole account)"
          data-testid="browse-bulk-delete"
        >
          <Icon name="trash" size={16} />{" "}
          {s.deletingAll ? `Deleting ${s.deletingAll.done}/${s.deletingAll.total}…` : `Delete (${s.checkedCount})`}
        </button>
      )}
    </div>
  );
}

/** The error banner, the local-action status line, the background-refetch hint and the
 *  first-load placeholder — four one-line states, identical on both pages. */
function SurfaceNotices({ s }: { s: TemplateSurface }) {
  const admin = s.mode === "admin";
  return (
    <>
      {s.error && (
        <p className="customer-templates__error banner" role="alert" data-testid={admin ? "templates-batch-error" : "customer-templates-error"}>
          <ErrorNotice detail={s.error} />
        </p>
      )}
      {s.localMsg && (
        <p className="panel-sub" role="status" data-testid="templates-local-msg">{s.localMsg}</p>
      )}
      {/* Background refetch feedback — the rows already on screen stay there, so a subtle
          inline hint replaces the full-screen loader (which only shows on first load). */}
      {s.loading && s.visibleCount > 0 && (
        <p className="ctpl-refreshing faint" data-testid="customer-templates-refreshing" aria-live="polite">
          Loading…
        </p>
      )}
      {/* Full-screen loading placeholder ONLY on the very first load (no templates at all
          yet). Gated on the UNFILTERED row count — NOT the filtered one — because a search
          that matches zero rows would otherwise replace the whole surface with a loader on
          every background refetch, losing the operator's selection. */}
      {s.firstLoad && (
        <EmptyState
          icon="image"
          title="Loading templates…"
          testId={admin ? "templates-loading" : "customer-templates-loading"}
        />
      )}
    </>
  );
}

/**
 * The results region — the count line, the select-all, the active chips and the rows.
 *
 * ONE array reaches ONE render prop (F-31): `shown` is the filter bar's output, the blocks,
 * the list, the bulk bar's pool and the count line's numerator.
 */
function SurfaceResults({ s }: { s: TemplateSurface }) {
  if (s.firstLoad) return null;
  return (
    <div className="browse">
      {/* `aria-describedby` points the results REGION at the count line, so a reader who
          lands on the results hears "Showing 24 of 137" as the region's description. */}
      <div
        className="browse__results"
        data-testid="browse-results"
        role="region"
        aria-label="Templates"
        aria-describedby={s.browseCountId}
      >
        <div className="browse__summary">
          {/* THE honest count. `M` is the rows LOADED, not the catalog — the day
              `listTemplates` becomes `listTemplatesPage`, this sentence changes in ONE
              place. `aria-live="polite"`: a filter change is ANNOUNCED, not only painted. */}
          <p className="browse__count faint" id={s.browseCountId} data-testid="browse-count" role="status" aria-live="polite">
            Showing {s.shown.length} of {s.loadedCount}
          </p>
          {/* M8 (Deel) — a header checkbox that selects every visible row, with the total
              stated plainly beside it. Tri-state: `indeterminate` when only some are ticked,
              which is the only way a header checkbox can tell the truth. */}
          {s.selecting && s.shown.length > 0 && (
            <label className="browse__selectall">
              <input
                type="checkbox"
                checked={s.allShownChecked}
                ref={(el) => { if (el) el.indeterminate = s.someShownChecked; }}
                onChange={s.toggleSelectAll}
                aria-label={`Select all ${s.shown.length} shown`}
                data-testid="browse-select-all"
              />
              <span>Select all {s.shown.length} shown</span>
            </label>
          )}
          {s.chips.length > 0 && (
            <div className="browse__chips" data-testid="browse-chips">
              {s.chips.map((c) => (
                <button
                  key={`${c.key}:${c.value}`}
                  type="button"
                  className="chip ctpl-chip browse__chip"
                  onClick={() => s.onFacetToggle(c.key, c.value)}
                  aria-label={c.removeLabel}
                  title={c.removeLabel}
                  data-testid={`browse-chip-${c.key}-${c.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c.label}
                  <Icon name="close" size={16} />
                </button>
              ))}
              <button
                type="button"
                className="link-btn browse__clear"
                onClick={s.onFacetClearAll}
                data-testid="browse-chips-clear"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
        {s.renderRows(s.shown)}
      </div>
    </div>
  );
}

/** The filter bar — search on top, a row of facet dropdowns under it, nothing beside the
 *  results at any width (WS-K5, the owner's own sentence). */
function SurfaceFilterBar({ s }: { s: TemplateSurface }) {
  return (
    <BrowseFilterBar
      groups={s.facetGroups}
      selection={s.facetSelection}
      onToggle={s.onFacetToggle}
      onClearGroup={s.onFacetClearGroup}
      search={{
        value: s.query,
        onChange: s.setQuery,
        placeholder: s.searchPlaceholder,
        testId: s.mode === "admin" ? "templates-search" : "ctpl-search",
      }}
    />
  );
}

/* ══════════════════════════ PAGE ONE — Templates, customer ══════════════════════════ */

/**
 * `#browse` — **pick and use**.
 *
 * Active templates only, a card grid by default, the four narrowings a customer can act on
 * (product type · brand · model · on this device), and ONE commit: take what is ticked into
 * the mock-up run. There is no status, no version, no approval and no authoring control on
 * this page — not hidden, absent, because `capabilitiesFor("customer", …)` makes every one
 * of them `false` (D-5).
 */
export function CustomerTemplatesSurface({ onGoExport }: {
  onGoExport?: (tab?: "select" | "batch") => void;
}) {
  const s = useTemplateSurface({ mode: "customer", onGoExport });
  return (
    <section className="customer-templates" data-testid="customer-templates">
      <header className="ctpl-head">
        <div className="ctpl-head__lead">
          {/* W4.10: this list IS the `#browse` route, so its title is the page title. */}
          <h1 className="ctpl-head__title panel-title">
            Templates{" "}
            <InfoHint text="Browse the available templates, then sync the ones you want to this device." />
          </h1>
          <SyncFreshness />
        </div>
        <div className="row ctpl-head__controls">
          {/* A4: no creation entry — a customer makes a template from Generate only. */}
          <SurfaceViewControls s={s} />
        </div>
      </header>

      <SurfaceFilterBar s={s} />
      <SurfaceNotices s={s} />
      <SurfaceResults s={s} />

      {/*
        * Z0 rule 7 — the page's commit, at the foot of the column.
        *
        * B4 allows ONE `.btn--primary` per surface and rule 7 says it lives here. On this
        * page that is "Use for mock-ups": the whole point of browsing is to take templates
        * into a run, and every other control on the page — Sync, Force sync, Clear — is a
        * step on the way to it. `ActionBar` warns in dev when its DOM ends up holding more
        * than one; the assertion that there is exactly one is this wave's own
        * (`browse-page.test.tsx`, "each page commits from its own .actionbar"), because a
        * dev-only console line is not a gate.
        */}
      <ActionBar
        label="Template actions"
        primary={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void s.sendCheckedToBatch()}
            disabled={s.sendingBatch || s.checkedCount === 0}
            data-testid="customer-templates-send-batch"
            title={s.checkedCount === 0 ? "Tick a template first — Select turns the row checkboxes on" : "Take the ticked templates into a mock-up run"}
          >
            {s.sendingBatch
              ? "Creating…"
              : `Use for mock-ups${s.checkedCount > 0 ? ` (${s.checkedCount} template${s.checkedCount === 1 ? "" : "s"})` : ""}`}
          </button>
        }
      >
        <SelectionCluster s={s} />
        <ForceSyncButton s={s} />
      </ActionBar>

      {s.preview}
      {s.forceSyncPicker}
    </section>
  );
}

/* ═══════════════════ PAGE TWO — Editor ▸ Templates, admin (authoring) ═══════════════════ */

/**
 * `#editor` ▸ Templates — **authoring**.
 *
 * Every status, every version, the approvals, the mask editor and the account-wide delete,
 * over a dense index table. It is NOT the customer page with extra buttons: it defaults to
 * the table rather than the grid, it carries Status and Source dropdowns the customer page
 * has no groups for, its row actions are the authoring set, and its ONE commit is making a
 * NEW template rather than using an existing one.
 */
export function AdminTemplatesSurface({
  onOpenEditor, onNewTemplate, pageTitle = true,
}: {
  onOpenEditor?: (t: ProductTemplate) => void;
  onNewTemplate?: () => void;
  pageTitle?: boolean;
}) {
  const s = useTemplateSurface({ mode: "admin", onOpenEditor, onNewTemplate });
  // W4.10: inside the Editor's Templates tab the route already owns an `<h1>`, so the heading
  // stands down a level. It cannot simply disappear — this header also names the surface for
  // a screen reader walking the Editor's tab panels.
  const Title = pageTitle ? "h1" : "h2";
  return (
    <section className="templates tpl-admin customer-templates" data-testid="templates-panel">
      <div className="card tpl-card">
        <header className="ctpl-head">
          <div className="ctpl-head__lead">
            <Title className="ctpl-head__title panel-title">
              Templates{" "}
              <InfoHint text="Create, edit and approve templates, then sync them to this device." />
            </Title>
            <SyncFreshness />
          </div>
          <div className="row ctpl-head__controls">
            <SurfaceViewControls s={s} />
          </div>
        </header>

        <SurfaceFilterBar s={s} />
        <SurfaceNotices s={s} />
        <SurfaceResults s={s} />

        {/*
          * Z0 rule 7. The authoring page's one commit is `New template` — it was the header's
          * primary, beside three ghosts, which is the arrangement rule 7 exists to end. The
          * bulk actions stay the quiet tier: a second volt fill the moment two rows are
          * checked is a state the gate's default walk never reaches, so a test asserts it.
          */}
        <ActionBar
          label="Template actions"
          primary={s.caps.create ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={s.onNewTemplate}
              data-testid="templates-generate"
            >
              <Icon name="sparkle" size={16}/> New template
            </button>
          ) : undefined}
        >
          <SelectionCluster s={s} />
          <ForceSyncButton s={s} />
        </ActionBar>
        {s.forceSyncPicker}
        {/*
          * NO PREVIEW DRAWER (WS-Z1 fix round 2, L-1). The drawer is the CUSTOMER page's way
          * into a template; this page's way in is the mask editor. `openRow` routes every row
          * here, `TemplatesPanel` — the only mount — always passes the callback, and
          * `AdminTemplatesSurface` is not public API, so the `{s.preview}` that used to sit
          * here rendered a composition no route and no export could reach. Owner rule A:
          * remove redundancy, never capability. The capability survives twice — the customer
          * drawer (`CustomerTemplatesSurface`) and this page's editor route.
          */}
      </div>
    </section>
  );
}

/**
 * The compatibility entry — `mode` picks the page.
 *
 * It exists because 60+ suites and WS-D2's layout probe mount "the template list" by mode,
 * and because `CustomerTemplates.tsx` / `TemplatesPanel.tsx` are the ROUTES' entry points
 * rather than the list's. It resolves to one of the two surfaces above and holds no state,
 * no layout and no branch of its own.
 */
export function TemplateListPage({ mode, onGoExport, onOpenEditor, onNewTemplate, pageTitle = true }: TemplateListPageProps) {
  return mode === "admin"
    ? <AdminTemplatesSurface onOpenEditor={onOpenEditor} onNewTemplate={onNewTemplate} pageTitle={pageTitle} />
    : <CustomerTemplatesSurface onGoExport={onGoExport} />;
}

/** Resolve a template's best-available preview image URL: the baked mockup preview, then the
 *  server-resolved row thumbnail (`previewRef`, available before sync), then the base-case
 *  image from a payload that happens to be present. Shared by the row/card thumbnail
 *  (`TemplateThumb`) and the preview drawer's fallback hero — one resolver, so the two
 *  surfaces never drift on which ref wins. */
function resolveTemplateRef(template: ProductTemplate): string | undefined {
  const baked = bakedPreviewUrl(template.currentVersion?.bakedAssets as BakedAssetRefs | null | undefined);
  return baked ?? template.previewRef ?? template.currentVersion?.payload?.views?.[0]?.base_case_image_ref ?? undefined;
}

/** A view image in the preview drawer that degrades to a placeholder if it 404s / is blocked
 *  (CORS), instead of showing a broken-image icon. */
function ViewImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (failed) return <div className="ctpl-view__placeholder" aria-hidden="true"><Icon name="image" size={16}/></div>;
  return <img className="ctpl-view__img" src={src} alt={alt} onError={() => setFailed(true)} />;
}

/** Detail / preview drawer for one template — its views, geometry and output settings (the
 *  cloud stores no rendered pixels). Opened by clicking a row.
 *
 *  Exported for `cross-surface-consistency.test.tsx`, which mounts it beside the row and the
 *  card so the three are compared LITERALLY for one fixture (review-1 m-2) rather than
 *  through the helpers each of them happens to call. It takes plain props and reads no
 *  services context, so that costs the test nothing. */
export function TemplatePreview({
  template, synced, busy, rendering, loading, fetchError, canRender, onRender, onSync, onUnsync, onClose,
}: {
  template: ProductTemplate;
  synced: boolean;
  busy: boolean;
  rendering: boolean;
  /** The A3 full-template fetch (for `payload.views`) is still in flight. */
  loading: boolean;
  /** The A3 full-template fetch failed — shown as a non-blocking inline notice; the drawer
   *  still renders with whatever fallback image is available. */
  fetchError: string | null;
  canRender: boolean;
  onRender: () => void;
  onSync: () => void;
  onUnsync: () => void;
  onClose: () => void;
}) {
  /* WS-Z8 (R1 adoption): this is `ui/Drawer`. It used to be a `.lightbox` div carrying
     `role="dialog" aria-modal="true"` and, under it, a hand-written copy of the dialog
     contract — an Escape listener on `window`, a Tab wrap, a focus-on-open effect keyed so a
     parent re-render could not re-run it, and a focus-return effect. All five now come from
     `ui/dialog.ts`, which the Drawer and the Modal share verbatim, so the whole block is
     gone: ~60 lines of chrome that the primitive already owned, plus the stacking rule that
     the local copy could never have (a dialog opened from inside this one now takes Escape,
     Tab and the overlay off it, and hands them back when it closes).

     `.lightbox` goes back to being image zoom — and with `wizard/Wizard.tsx` on `ui/Modal`
     `bare`, nothing renders it at all, so its CSS leaves `styles/components.css` too. */
  const m = rowMeta(template);
  const views = template.currentVersion?.payload?.views ?? [];
  const settings = template.currentVersion?.payload?.render_settings;
  // The row-thumbnail preview chain (baked preview → previewRef → first view ref). Computed
  // UNCONDITIONALLY: it used to be suppressed the moment `payload.views` arrived, which made
  // the drawer strictly WORSE than the row it was opened from — wizard-created templates
  // (whose views carry no hosted `base_case_image_ref`) flipped from a visible image to a
  // grid of placeholders when the full fetch landed (the "preview images missing" bug).
  const keyedOnly = useKeyedPreviewsOnly();
  const heroRef = keyedOnly ? keyedPreviewUrl(template) : resolveTemplateRef(template);
  const heroHosted = typeof heroRef === "string" && /^https?:\/\//.test(heroRef);
  // Per-view preview source: the view's own hosted reference, else ITS baked generated image
  // (per-view refs win over flat via refsForView), else the hero chain above.
  const bakedMap = template.currentVersion?.bakedAssets as BakedAssetRefsMap | null | undefined;
  const viewSrc = (viewType: string, baseRef?: string | null): string | undefined => {
    const own = typeof baseRef === "string" && /^https?:\/\//.test(baseRef) ? baseRef : undefined;
    const bakedRefs = bakedMap ? refsForView(bakedMap, viewType) : undefined;
    const baked = bakedRefs?.preview_url || bakedRefs?.generated_url;
    const bakedHosted = typeof baked === "string" && /^https?:\/\//.test(baked) ? baked : undefined;
    // A4: the keyed mock-up first — `own` is the raw case photo, the magenta image for a
    // generated template, which a customer never sees.
    return bakedHosted ?? (keyedOnly ? undefined : own) ?? (heroHosted ? heroRef : undefined);
  };
  return (
    <Drawer
      testid="customer-template-preview"
      // Rule A: the suite reaches for this control by name — including the assertion that
      // focus lands on it when the drawer opens — so it keeps the id it had, rather than
      // gaining a second Close in the footer to carry it.
      closeTestid="customer-template-preview-close"
      title={template.name}
      /* The SAME pill the table row renders — class AND label. The drawer used to print the
         raw status, so one Pending template read "Pending approval" in the list and
         "Pending" in the drawer it opened. It is the Drawer's `badge` slot now, which is
         where a status pill goes on both dialog shapes (§6). */
      badge={m.status ? <span className={`badge ${statusBadgeClass(m.status)}`}>{statusLabel(m.status)}</span> : undefined}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={synced ? onUnsync : onSync}
            disabled={busy}
            data-testid="customer-template-preview-sync"
            title={synced ? "Remove from this device" : "Sync to this device"}
          >
            {busy ? "Working…" : synced ? <><Icon name="check" size={16}/> Synced — click to Unsync</> : "Sync to this device"}
          </button>
          {canRender && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={onRender}
              disabled={rendering}
              data-testid="customer-template-preview-render"
              title="Create a mock-up in Export"
            >
              {rendering ? "Creating…" : "Create Mock-up"}
            </button>
          )}
        </>
      }
    >
      {loading && (
        <p className="ctpl-refreshing faint" data-testid="customer-template-preview-loading" aria-live="polite">
          Loading preview…
        </p>
      )}
      {fetchError && (
        <p className="customer-templates__error banner" role="alert" data-testid="customer-template-preview-error">
          <ErrorNotice detail={fetchError} />
        </p>
      )}

      <dl className="ctpl-facts">
        <div><dt>Product</dt><dd>{m.product}</dd></div>
        <div><dt>Model</dt><dd>{m.model}</dd></div>
        <div><dt>Style</dt><dd>{m.style}</dd></div>
        <div><dt>Output</dt><dd>{m.format}{settings ? ` · ${settings.output_size[0]}×${settings.output_size[1]} · ${settings.fit_policy}` : ""}</dd></div>
        <div><dt>Views</dt><dd>{m.views.length || views.length || "—"}</dd></div>
      </dl>

      {views.length > 0 ? (
        <div className="ctpl-views">
          {views.map((v, i) => (
            <figure className="ctpl-view" key={i}>
              {viewSrc(v.view_type, v.base_case_image_ref) ? (
                <ViewImage src={viewSrc(v.view_type, v.base_case_image_ref)!} alt={`${v.view_type} view`} />
              ) : (
                <div className="ctpl-view__placeholder" aria-hidden="true"><Icon name="image" size={16}/></div>
              )}
              <figcaption>
                {humanize(v.view_type) || "—"}
                {v.brand_logo_placement ? " · logo" : ""}
                {v.masks?.length ? ` · ${v.masks.length} mask${v.masks.length === 1 ? "" : "s"}` : ""}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : heroHosted ? (
        // No fetched view geometry (pending, failed, or genuinely payload-less) but a
        // hosted preview image exists — show it as a single hero rather than a blank
        // drawer. Same ref chain the row thumbnail uses (baked preview → previewRef).
        <div className="ctpl-views">
          <figure className="ctpl-view" data-testid="customer-template-preview-hero">
            <ViewImage src={heroRef!} alt={`${template.name} preview`} />
            <figcaption>
              Preview image
              {m.views.length > 0 ? ` · ${m.views.length} view${m.views.length === 1 ? "" : "s"}` : ""}
            </figcaption>
          </figure>
        </div>
      ) : m.views.length > 0 ? (
        <div className="ctpl-viewchips">
          {m.views.map((v, i) => <span className="badge badge--neutral" key={i}>{humanize(v) || "—"}</span>)}
        </div>
      ) : (
        <p className="muted">Sync this template to your device to create and preview the mock-up.</p>
      )}
    </Drawer>
  );
}
